import { useEffect, useMemo, useState } from "react";
import { presignFileDownload } from "../services/uploadServices";
import {
  buildAttachmentFileURL,
  buildInlineFileURL,
  FileProxyMode,
} from "../utils/fileProxy";
import { isBrowserReachableFileURL } from "../utils/fileAccessURL";

const downloadMode = (process.env.REACT_APP_DOWNLOAD_MODE ?? "proxy").toLowerCase();
const cacheSkewMs = 30 * 1000;

const presignedCache = new Map<string, { url: string; expiresAtMs: number }>();

const buildProxyURL = (mode: FileProxyMode, reference?: string | null) =>
  mode === "attachment"
    ? buildAttachmentFileURL(reference)
    : buildInlineFileURL(reference);

const buildCacheKey = (
  mode: FileProxyMode,
  fileMetadataId?: string | null,
  reference?: string | null
) => `${mode}:${fileMetadataId ?? ""}:${reference ?? ""}`;

const isStorageKeyReference = (reference: string) => reference.includes("/");

const getCachedURL = (cacheKey: string) => {
  const cached = presignedCache.get(cacheKey);
  if (!cached) {
    return null;
  }
  if (cached.expiresAtMs <= Date.now() + cacheSkewMs) {
    presignedCache.delete(cacheKey);
    return null;
  }
  if (!isBrowserReachableFileURL(cached.url)) {
    presignedCache.delete(cacheKey);
    return null;
  }
  return cached.url;
};

interface UseFileAccessURLOptions {
  mode?: FileProxyMode;
  fileMetadataId?: string | null;
  reference?: string | null;
  enabled?: boolean;
}

export const useFileAccessURL = ({
  mode = "inline",
  fileMetadataId,
  reference,
  enabled = true,
}: UseFileAccessURLOptions) => {
  const proxyURL = useMemo(() => buildProxyURL(mode, reference), [mode, reference]);
  const [resolvedURL, setResolvedURL] = useState(proxyURL);

  useEffect(() => {
    const trimmedReference = reference?.trim() ?? "";
    const trimmedFileMetadataID = fileMetadataId?.trim() ?? "";

    if (!enabled || downloadMode !== "presigned" || (!trimmedReference && !trimmedFileMetadataID)) {
      setResolvedURL(proxyURL);
      return;
    }

    const cacheKey = buildCacheKey(mode, trimmedFileMetadataID, trimmedReference);
    const cachedURL = getCachedURL(cacheKey);
    if (cachedURL) {
      setResolvedURL(cachedURL);
      return;
    }

    let cancelled = false;
    const load = async () => {
      const response = await presignFileDownload(
        {
          fileMetadataId: trimmedFileMetadataID || undefined,
          fileId: trimmedReference && !isStorageKeyReference(trimmedReference) ? trimmedReference : undefined,
          storageKey: trimmedReference && isStorageKeyReference(trimmedReference) ? trimmedReference : undefined,
          disposition: mode,
        }
      );

      if (cancelled || response.data === false) {
        if (!cancelled) {
          setResolvedURL(proxyURL);
        }
        return;
      }

      if (!isBrowserReachableFileURL(response.data.url)) {
        setResolvedURL(proxyURL);
        return;
      }

      const expiresAtMs = Number.isNaN(Date.parse(response.data.expiresAt))
        ? Date.now()
        : Date.parse(response.data.expiresAt);
      presignedCache.set(cacheKey, {
        url: response.data.url,
        expiresAtMs,
      });
      setResolvedURL(response.data.url);
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [enabled, fileMetadataId, mode, proxyURL, reference]);

  return resolvedURL || proxyURL;
};
